import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/styles';
import { isArray, isObject, isString, isBoolean } from 'lodash';
import {
  TextField,
  Typography,
  Collapse,
  Grid,
  Button,
  Divider,
  Paper,
  Tooltip,
  IconButton
} from '@material-ui/core';
import { AddBox, Delete, ArrowRight, ArrowDropDown } from '@material-ui/icons';
import ErrorIcon from '@material-ui/icons/Error';
import UserMessageSnackbar from 'views/UserMessaging/customModalContent/userMessageSnackbar';
import { SelectInput } from 'components';

const useStyles = makeStyles(theme => ({
  root: {},
  content: {
    padding: theme.spacing(2)
  },
  emptyArray: {
    fontSize: '14px',
    color: '#b7b7b7'
  },
  customError: {
    color: theme.palette.error.main
  },
  cursor: {
    cursor: 'pointer'
  },
  input: {
    overflowWrap: 'break-word',
    flexGrow: '0',
    maxWidth: '17.44%',
    flexBasis: '17.44%'
  },
  inputKey: {
    padding: '4px',
    color: 'rgb(110,110,110)',
    fontSize: '12px',
    fontWeight: '500'
  },
  payloadInput: {
    width: '700px',
    padding: '12px'
  },
  payloadButton: {
    marginTop: '12px',
    display: 'flex',
    justifyContent: 'end'
  },
  inputStyle: {
    padding: '8px'
  },
  requiredField: {
    color: '#e53935'
  },
  array: {
    padding: '8px 8px 16px 8px'
  },
  string: {
    marginLeft: '20px',
    marginTop: '8px'
  },
  stringKey: {
    paddingTop: '4px'
  },
  stringInput: {
    padding: '0px 16px 8px 16px'
  },
  objectInput: {
    marginLeft: '32px',
    marginTop: '6px',
    marginBottom: '6px'
  },
  emptyArrayInput: {
    marginLeft: '20px',
    marginBottom: '4px'
  },
  arrayInput: {
    marginLeft: '50px',
    padding: '8px'
  },
  fullWidth: {
    width: '100%'
  }
}));

const JsonEditor = props => {
  const { schema } = props;
  const {
    schemaId,
    schemaObject,
    schemaObjectCopy,
    handleObjectChange,
    shouldShowError,
    getErrorMessage,
    onFocus,
    handleBlur,
    validationErrors,
    validate,
    validateKeys,
    validateEventType,
    validateJsonPayload,
    isInvalidPayload,
    enrichmentProducts,
    disabledInputs,
    requiredFields,
    schemaSaveFailed,
    enrichmentSchemas
  } = schema;
  const classes = useStyles();

  const eventTypes = useSelector(store => store.schema.eventTypes);

  let collapseObject = {};
  const [open, setOpen] = React.useState(false);
  const [openKey, setOpenKey] = React.useState(collapseObject);
  const [openSnackbar, setOpenSnackbar] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState('');
  const [messageSeverity, setMessageSeverity] = React.useState('');
  const [isPayloadInvalid, setIsPayloadInvalid] = React.useState(false);

  useEffect(() => {
    if (schemaSaveFailed) {
      let keys = typeof validateKeys === 'function' ? validateKeys() : {};
      let errors = { ...validationErrors, ...keys };
      Object.entries(errors).forEach(entry => {
        let errorKey = entry[0];
        let value = entry[1];
        if (value.showError) {
          let keyLength = errorKey.split('.').length;
          if (keyLength > 1) {
            let keys = errorKey.split('.', keyLength - 1);
            let key = '';
            keys.forEach(k => {
              key = key === '' ? k : key.concat('.' + k);
              openKey[key] = true;
            });
          } else {
            openKey[errorKey] = true;
          }
        }
      });
      setOpenKey({ ...openKey });
    }
  }, [validationErrors]);

  const handleClick = (key, isDeleted) => {
    let keys = key.split('.');
    if (isDeleted) {
      if (keys.indexOf('eventTypeUniqueUpdateKey') >= 0) {
        validateKeys();
      }
      if (keys.indexOf('eventTypeGroup') >= 0) {
        validateEventType(key);
      }
      Object.keys(openKey)
        .filter(k => k.split('.').indexOf(key) >= 0)
        .forEach(k => {
          openKey[k] = false;
        });
      setOpen(false);
      openKey[key] = false;
      setOpenKey({ ...openKey });
    } else {
      setOpen(!open);
      openKey[key] = !openKey[key];
      setOpenKey({ ...openKey });
    }
  };

  const shouldDisabledInput = keys => {
    return (
      (schemaId &&
        keys[keys.length - 1] === 'schemaToEventPartialCollectionName') ||
      disabledInputs.indexOf(keys[keys.length - 1]) >= 0
    );
  };

  const getAvailableEventTypeSchemas = data => {
    return (
      eventTypes
        ?.filter(
          type => type.eventTypeCategory === schemaObject['eventTypeCategory']
        )
        .filter(
          type =>
            schemaObject['eventTypeGroup']
              .map(eventTypeGroup => eventTypeGroup.eventTypeSchema)
              .indexOf(type.eventType) < 0 || data === type.eventType
        ) || []
    );
  };

  const shouldDisableAddButton = key => {
    switch (key) {
      case 'eventTypeGroup':
        let validEventTypes = eventTypes?.filter(
          type => type.eventTypeCategory === schemaObject['eventTypeCategory']
        );
        return !validEventTypes ||
          (schemaObject['eventTypeGroup'].length === validEventTypes.length);
      case 'schemaEnrichmentData':
        return !enrichmentProducts ||
          (schemaObject['schemaEnrichmentData'].length === enrichmentProducts.length);
      default:
        return false;
    }
  };

  const shouldShowErrorIcon = input => {
    return (
      (input === 'emailAddressPath' &&
        validationErrors.emailAddressPath?.showError) ||
      (input === 'phoneNumberPath' &&
        validationErrors.phoneNumberPath?.showError)
    );
  };

  const getDisplayName = (entry, index) => {
    let name = '';
    let keys = Object.keys(entry);
    let values = Object.values(entry);
    if (keys.indexOf('displayName') >= 0) {
      name =
        values[keys.indexOf('displayName')] !== ''
          ? values[keys.indexOf('displayName')]
          : '[ displayName ]';
    } else if (keys.indexOf('eventTypeDescription') >= 0) {
      name =
        values[keys.indexOf('eventTypeDescription')] !== ''
          ? values[keys.indexOf('eventTypeDescription')]
          : '[ eventTypeDescription ]';
    } else if (keys.indexOf('enrichmentProduct') >= 0) {
      name =
        values[keys.indexOf('enrichmentProduct')] !== ''
          ? values[keys.indexOf('enrichmentProduct')]
          : '[ enrichmentProduct ]';
    }
    return index + 1 + '. ' + name;
  };

  const validateJson = () => {
    if (validateJsonPayload()) {
      setIsPayloadInvalid(false);
      setOpenSnackbar(true);
      setSnackbarMessage('Valid JSON payload');
      setMessageSeverity('success');
    } else {
      setIsPayloadInvalid(true);
      setOpenSnackbar(true);
      setSnackbarMessage('Invalid JSON payload');
      setMessageSeverity('error');
    }
  };

  const setInputValue = (event, key) => {
    let keys = key.split('.');
    let element = schemaObject;
    for (let i = 0; i < keys.length - 1; i++) {
      if (keys[i] !== '__proto__' && keys[i] !== 'constructor' && keys[i] !== 'prototype') {
        element = element[keys[i]];
      }
    }
    element[keys[keys.length - 1]] = event.target.value;
    handleObjectChange({ ...schemaObject });
    if (key === 'examplePayload' && event.target.value === '') {
      setIsPayloadInvalid(false);
      validateJsonPayload();
    }
  };

  const addElement = (array, key) => {
    let keys = key.split('.');
    let element = JSON.parse(JSON.stringify(schemaObjectCopy));
    for (let i = 0; i < keys.length;) {
      if (isArray(element[keys[i]])) {
        element = element[keys[i]][0];
        i = i + 2;
      } else {
        if (keys[i++] !== '__proto__' && keys[i++] !== 'constructor' && keys[i++] !== 'prototype') {
          element = element[keys[i++]];
        }
      }
    }
    array.push(element);
    handleObjectChange({ ...schemaObject });
  };

  const removeElement = key => {
    let keys = key.split('.');
    let element = schemaObject;
    for (let i = 0; i < keys.length; i++) {
      if (i === keys.length - 1) {
        if (isArray(element)) {
          element.splice(keys[i], 1);
        } else {
          element[keys[i]].splice(0, element[keys[i]].length);
        }
      } else if (keys[i] !== '__proto__' && keys[i] !== 'constructor' && keys[i] !== 'prototype') {
          element = element[keys[i]];
      }
    }
    handleClick(key, true);
    handleObjectChange({ ...schemaObject });
  };

  const getArrayArrowIcons = path => {
    return openKey[path] ? (
      <ArrowDropDown
        className={classes.cursor}
        fontSize="small"
        onClick={() => {
          handleClick(path);
        }}
      />
    ) : (
      <ArrowRight
        className={classes.cursor}
        fontSize="small"
        onClick={() => {
          handleClick(path);
        }}
      />
    );
  };

  const getDeleteIcon = path => {
    return (
      <Delete
        className={classes.cursor}
        onClick={() => {
          removeElement(path);
        }}
        color="error"
        fontSize="small"
      />
    );
  };

  const getAddIcon = (entry, path) => {
    return (
      <Grid item style={{ padding: '0px', paddingLeft: '8px' }}>
        <Tooltip
          title={
            shouldDisableAddButton(path) ? 'Maximum elements added' : 'Add'
          }>
          <span>
            <IconButton
              style={{ padding: '0px' }}
              disabled={shouldDisableAddButton(path)}
              onClick={() => {
                addElement(entry[1], path);
                entry[0] === 'eventTypeGroup' && validate(false);
              }}>
              <AddBox
                className={classes.cursor}
                color={shouldDisableAddButton(path) ? 'disabled' : 'primary'}
                fontSize="small"
              />
            </IconButton>
          </span>
        </Tooltip>
      </Grid>
    );
  };

  const getExamplePayloadInput = (data, parentKey) => {
    return (
      <Paper
        className={classes.payloadInput}
        sx={{
          p: '2px 4px',
          display: 'flex',
          alignItems: 'center',
          width: 400
        }}>
        <TextField
          style={{ width: '675px' }}
          size="small"
          placeholder="Enter a value"
          multiline
          variant="outlined"
          rows={5}
          maxRows={5}
          value={data}
          onFocus={onFocus}
          onChange={event => {
            setInputValue(event, parentKey);
          }}
          name={parentKey}
          onBlur={() => {
            if (data === '') {
              setIsPayloadInvalid(false);
              validateJsonPayload();
            }
          }}
          error={isPayloadInvalid || isInvalidPayload}
          helperText={
            (isPayloadInvalid || isInvalidPayload) &&
            '"examplePayload" contains an invalid value'
          }
        />
        <Divider />
        <div className={classes.payloadButton}>
          <Button
            size="small"
            color="primary"
            variant="contained"
            disabled={data.length === 0}
            onClick={validateJson}>
            Validate
          </Button>
        </div>
      </Paper>
    );
  };

  const getStringInputData = (parentKey, keys) => {
    return {
      required: true,
      variant: 'outlined',
      size: 'small',
      placeholder: 'Enter a value',
      fullWidth: true,
      name: parentKey,
      disabled: shouldDisabledInput(keys || []),
      onFocus: onFocus,
      onBlur: event => handleBlur(event, parentKey),
      key: parentKey,
      error: shouldShowError(parentKey),
      helperText: getErrorMessage(parentKey) || ''
    };
  };

  const getEventTypeOptions = data => {
    let options = [];
    if (data === '') {
      options = [
        {
          eventType: 'Select an eventType',
          isDisabled: 'true'
        }
      ];
    }
    options = [...options, ...getAvailableEventTypeSchemas(data)];
    return {
      list: options,
      keyId: 'eventType',
      valueId: 'eventType',
      displayValueId: 'eventType'
    };
  };

  const getEventTypeSchemaInput = (data, parentKey) => {
    return (
      <SelectInput
        {...getStringInputData(parentKey)}
        inputProps={{
          style: {
            height: '32px'
          }
        }}
        value={data || 'Select an eventType'}
        onChange={event => {
          setInputValue(event, parentKey);
          handleBlur(event);
        }}
        options={getEventTypeOptions(data)}
      />
    );
  };

  const getEnrichmentProductOptions = (data, defaultValue) => {
    let options = [];
    if (data === '') {
      options.push({
        value: defaultValue,
        isDisabled: 'true'
      });
    }
    enrichmentProducts.map(product => {
      options.push({ value: product });
    });
    return {
      list: options,
      keyId: 'value',
      valueId: 'value',
      displayValueId: 'value'
    };
  };

  const getEnrichmentProductInput = (data, parentKey) => {
    let keys = parentKey.split('.');
    let defaultValue = (enrichmentProducts && enrichmentProducts.length > 0) ? 'Select an Enrichment product' : 'No options';
    return (
      <SelectInput
        {...getStringInputData(parentKey, keys)}
        inputProps={{
          style: {
            height: '32px'
          }
        }}
        value={data || defaultValue}
        onChange={event => {
          setInputValue(event, parentKey);
          handleBlur(event);
        }}
        options={getEnrichmentProductOptions(data, defaultValue)}
      />
    );
  };

  function getSelectedEnrichmentProduct(parentKeyPath) {
    let enrichmentIndex = parentKeyPath?.split('.')?.length > 1 ? parentKeyPath?.split('.')[1] : null;
    let enrichmentData = enrichmentIndex ? schemaObject.schemaEnrichmentData[enrichmentIndex] : {};
    return enrichmentData?.enrichmentProduct;
  }

  const getVaildEnrichmentUuidOptions = (data, validSchemas, defaultValue) => {
    let options = [];
    if (data === '') {
      options.push({
        value: defaultValue,
        isDisabled: 'true'
      });
    }
    validSchemas.map(schema => {
      options.push({ value: schema.uuid });
    });
    return {
      list: options,
      keyId: 'value',
      valueId: 'value',
      displayValueId: 'value'
    };
  };

  const getEnrichmentUuidsInput = (data, parentKey) => {
    let keys = parentKey.split('.');
    let selectedEnrichmentProduct = getSelectedEnrichmentProduct(parentKey);
    let validSchemas = enrichmentSchemas?.filter(schema =>
      schema.enrichmentProduct === selectedEnrichmentProduct &&
      (schema.active || schema.uuid === data)) || [];
    let defaultValue = (validSchemas && validSchemas.length > 0) ? 'Select an Schema Enrichment UUID' : 'No options';
    return (
      <SelectInput
        {...getStringInputData(parentKey, keys)}
        inputProps={{
          style: {
            height: '32px'
          }
        }}
        value={data || defaultValue}
        onChange={event => {
          setInputValue(event, parentKey);
          handleBlur(event);
        }}
        options={getVaildEnrichmentUuidOptions(data, validSchemas, defaultValue)}
      />
    );
  };

  const getStringInput = (data, parentKey) => {
    let keys = parentKey.split('.');
    return (
      <TextField
        {...getStringInputData(parentKey, keys)}
        value={data}
        InputProps={{
          style: {
            height: '32px'
          }
        }}
        style={{ background: shouldDisabledInput(keys) && '#ebebeb' }}
        onChange={event => {
          setInputValue(event, parentKey);
        }}></TextField>
    );
  };

  const createStringInput = (data, parentKey) => {
    let keys = parentKey.split('.');
    if (keys.indexOf('examplePayload') >= 0) {
      return getExamplePayloadInput(data, parentKey);
    } else if (keys.indexOf('eventTypeSchema') >= 0) {
      return getEventTypeSchemaInput(data, parentKey);
    } else if (keys.indexOf('enrichmentProduct') >= 0) {
      return getEnrichmentProductInput(data, parentKey);
    } else if (keys.indexOf('schemaEnrichmentUuid') >= 0) {
      return getEnrichmentUuidsInput(data, parentKey);
    } else {
      return getStringInput(data, parentKey);
    }
  };

  const getBooleanInputs = () => {
    let options = [
      {
        value: true,
        displayValue: 'True',
        customStyles: { padding: '8px 24px' }
      },
      {
        value: false,
        displayValue: 'False',
        customStyles: { padding: '8px 24px' }
      }
    ];
    return {
      list: options,
      keyId: 'value',
      valueId: 'value',
      displayValueId: 'displayValue'
    };
  };

  const createBooleanInput = (data, key) => {
    return (
      <SelectInput
        size="small"
        value={data}
        onChange={event => {
          setInputValue(event, key);
        }}
        inputProps={{
          style: {
            height: '32px'
          }
        }}
        options={getBooleanInputs()}
      />
    );
  };

  const createString = (entry, parentKeyPath) => {
    let key = entry[0];
    return (
      (key !== 'schemaEnrichmentUuid' || getSelectedEnrichmentProduct(parentKeyPath)) &&
      <Grid
        container
        spacing={2}
        className={classes.inputStyle}
        alignItems="baseline">
        <Grid item className={classes.input}>
          <Typography>
            {key}
            <span className={classes.requiredField}>
              {requiredFields.indexOf(key) >= 0 && ' * '}
            </span>
            {': '}
          </Typography>
        </Grid>
        <Grid item xs={3}>
          {createStringInput(entry[1], parentKeyPath + key)}
        </Grid>
        {shouldShowErrorIcon(key) && (
          <Grid item>
            <Tooltip title="Please fill at least one of emailAddressPath/phoneNumberPath to save this schema.">
              <ErrorIcon className={classes.requiredField} size="small" />
            </Tooltip>
          </Grid>
        )}
      </Grid>
    );
  };

  const createBoolean = (entry, parentKeyPath) => {
    return (
      <Grid
        container
        spacing={2}
        className={classes.inputStyle}
        alignItems="baseline">
        <Grid item className={classes.input}>
          <Typography>{entry[0] + ': '}</Typography>
        </Grid>
        <Grid item style={{ padding: '6px 8px' }}>
          {createBooleanInput(entry[1], parentKeyPath + entry[0])}
        </Grid>
      </Grid>
    );
  };

  const createArray = (entry, parentKeyPath) => {
    let path = parentKeyPath + entry[0];
    return (
      <Grid container spacing={2} className={classes.array}>
        <Grid item style={{ padding: '0px' }}>
          {getArrayArrowIcons(path)}
        </Grid>
        <Grid item style={{ padding: '4px 8px 0px 4px' }}>
          <Typography>
            {entry[0]}
            {' [ ' + entry[1].length + ' ] '}
          </Typography>
        </Grid>
        {getAddIcon(entry, path)}
        <Grid item style={{ padding: '0px', paddingLeft: '10px' }}>
          <Tooltip title="Remove all">{getDeleteIcon(path)}</Tooltip>
        </Grid>
        <Grid item>
          {shouldShowError(path) && (
            <Typography
              className={classes.customError}
              variant="caption"
              display="block">
              {getErrorMessage(path)}
            </Typography>
          )}
        </Grid>
      </Grid>
    );
  };

  const createStringArray = (entry, parentKeyPath, data, index) => {
    let path = parentKeyPath + entry[0] + '.' + index;
    return (
      <Grid container className={classes.string}>
        <Grid item className={classes.stringKey}>
          <Typography>{index + 1 + '. '}</Typography>
        </Grid>

        <Grid item className={classes.stringInput}>
          {createStringInput(data, path)}
        </Grid>
        <Grid item>{getDeleteIcon(path)}</Grid>
      </Grid>
    );
  };

  const createObjectArray = (entry, parentKeyPath, data, index) => {
    let path = parentKeyPath + entry[0] + '.' + index;
    return (
      <Grid container>
        <Grid container style={{ padding: '2px' }}>
          <Grid item>{getArrayArrowIcons(path)}</Grid>
          <Grid item className={classes.input}>
            <Typography className={classes.inputKey}>
              {getDisplayName(data, index)}
            </Typography>
          </Grid>
          <Grid item style={{ padding: '0px', paddingLeft: '8px' }}>
            {getDeleteIcon(path)}
          </Grid>
        </Grid>

        {openKey[path] && (
          <Grid container className={classes.objectInput}>
            <Grid item className={classes.fullWidth}>
              <Collapse in={openKey[path]} timeout="auto" unmountOnExit>
                {createObjectInput(data, path + '.')}
              </Collapse>
            </Grid>
          </Grid>
        )}
      </Grid>
    );
  };

  const createArrayInput = (entry, parentKeyPath) => {
    return (
      <Collapse
        in={openKey[parentKeyPath + entry[0]]}
        timeout="auto"
        unmountOnExit
        className={classes.fullWidth}>
        {entry[1].length === 0 && (
          <Grid container spacing={2} className={classes.emptyArrayInput}>
            <Grid item className={classes.emptyArray}>
              <span>[empty array]</span>
            </Grid>
          </Grid>
        )}
        {entry[1].length !== 0 &&
          entry[1].map((e, i) => (
            <React.Fragment key={parentKeyPath + i + 'array'}>
              {!isString(e) && createObjectArray(entry, parentKeyPath, e, i)}
              {isString(e) && createStringArray(entry, parentKeyPath, e, i)}
            </React.Fragment>
          ))}
      </Collapse>
    );
  };

  const createObject = (entry, parentKeyPath) => {
    return (
      <React.Fragment>
        {openKey[parentKeyPath + entry[0]] && (
          <Grid container spacing={2}>
            {!isArray(entry[1]) && (
              <Grid container>
                {createObjectInput(entry[1], parentKeyPath + entry[0])}
              </Grid>
            )}

            {isArray(entry[1]) && (
              <Grid container className={classes.arrayInput}>
                {createArrayInput(entry, parentKeyPath)}
              </Grid>
            )}
          </Grid>
        )}
      </React.Fragment>
    );
  };

  function showInput(entry) {
    return entry[0] !== "schemaEnrichmentData" || !schemaObject['internal'];
  }

  const createObjectInput = (data, parentKeyPath) => {
    return (
      <React.Fragment>
        {Object.entries(data)
          .sort()
          .map(entry => (
            <React.Fragment key={parentKeyPath + entry[0]}>
              {isString(entry[1]) && createString(entry, parentKeyPath)}
              {isBoolean(entry[1]) && createBoolean(entry, parentKeyPath)}
              {isArray(entry[1]) && showInput(entry) && createArray(entry, parentKeyPath)}
              {isObject(entry[1]) && showInput(entry) && createObject(entry, parentKeyPath)}
            </React.Fragment>
          ))}
      </React.Fragment>
    );
  };

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        {createObjectInput(schemaObject, '')}
      </div>
      <UserMessageSnackbar
        openSnackbar={openSnackbar}
        message={snackbarMessage}
        severity={messageSeverity}></UserMessageSnackbar>
    </div>
  );
};

export default JsonEditor;
